/**
 * Team Management - Team Leader & Admin Hub
 * Layer 2 of the 2-Layer Dashboard Model (Admin/TL Only)
 * 
 * Design: TitleFlow blue palette, matching Orders/Dashboard UI
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInShell } from '../shared/ModuleShell';
import axios from 'axios';
import { 
  Users, 
  Calendar, 
  Shield,
  ChevronLeft,
  CheckCircle,
  UserCheck,
  AlertCircle,
  ChevronRight,
  Activity,
  UserPlus,
  Eye,
  Coffee
} from 'lucide-react';

import LeaveApproval from './LeaveApproval';
import TeamLeaveCalendar from './team-calendar/TeamLeaveCalendar';
import LeaveBlocks from './LeaveBlocks';
import { TeamSessionStatus } from '../session';
import ITBreakManagement from '../it/ITBreakManagement';
import './TeamManagementDark.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const TEAM_TABS = [
  { id: 'approvals', label: 'Leave Approvals', icon: CheckCircle, description: 'Pending requests' },
  { id: 'calendar', label: 'Team Calendar', icon: Calendar, description: "Who's on leave" },
  { id: 'blocks', label: 'Block Dates', icon: Shield, description: 'Restrict leave periods' },
  // TEAM-AVAIL-REMOVAL (Aug 18, 2026): Team Availability tab removed —
  // redundant with Operations → Team Activity and Admin → Active Users.
  // The /api/attendance/team fetch stays: it powers the Team Summary card
  // (Team Size / Online Now) and the header online counter.
  { id: 'session-status', label: 'Session Health', icon: Activity, description: 'Activity monitoring' }
];
// IT-BREAK-MGMT-001: IT Operations Center only (TLs/HR already have the Break Management pages in their shells)
const IT_BREAK_TAB = { id: 'breaks', label: 'Break Management', icon: Coffee, description: 'Team break status & history' };

const TeamManagement = ({ currentUser, onNavigateBack, embedded: embeddedProp = false, darkSkin = false, effectiveScope = null }) => {
  const inShell = useInShell();
  const embedded = embeddedProp || inShell; // UNIFIED-SHELL-001
  const tabs = useMemo(() => (darkSkin ? [...TEAM_TABS, IT_BREAK_TAB] : TEAM_TABS), [darkSkin]);
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabs.some(t => t.id === urlTab) ? urlTab : 'approvals');
  useEffect(() => { if (tabs.some(t => t.id === urlTab)) setActiveTab(urlTab); else if (inShell && !urlTab) setActiveTab('approvals'); }, [urlTab, inShell, tabs]);
  // TM-NAV-FLICKER-001: panels stay mounted once visited — switching tabs only toggles visibility
  const [visited, setVisited] = useState(() => new Set([activeTab]));
  useEffect(() => { setVisited((v) => (v.has(activeTab) ? v : new Set([...v, activeTab]))); }, [activeTab]);
  const panel = (id) => ({ hidden: activeTab !== id, 'data-tab-panel': id, 'data-active': activeTab === id || undefined });
  const mounted = (id) => visited.has(id) || activeTab === id;
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [onBehalfSignal, setOnBehalfSignal] = useState(0);

  const fetchTeamData = useCallback(async () => {
    try {
      setLoadingTeam(true);
      
      const [attendanceRes, pendingRes] = await Promise.all([
        axios.get(`${API}/api/attendance/team`),
        axios.get(`${API}/api/leave/pending-approvals?limit=1`)
      ]);
      
      setTeamAttendance(attendanceRes.data.team_attendance || []);
      setPendingCount(pendingRes.data.total || 0);
    } catch (err) {
      console.error('Error fetching team data:', err);
    } finally {
      setLoadingTeam(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamData();
    const interval = setInterval(fetchTeamData, 60000);
    return () => clearInterval(interval);
  }, [fetchTeamData]);

  const formatDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return '--';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const loggedInCount = teamAttendance.filter(m => m.is_logged_in).length;
  const totalTeam = teamAttendance.length;

  return (
    <div
      className={embedded ? `${darkSkin ? 'tm-dark ' : ''}min-h-full pb-4` : 'min-h-screen bg-slate-50'}
      data-testid="team-management"
    >
      {/* TEAM-SHELL-001: embedded mode renders inside the role's portal
          shell (IT Ops / HR Portal) — compact in-content header instead of
          the full-screen sticky TitleFlow header. */}
      {/* TEAM-CAL-UI-001: the premium Team Calendar renders its own header — suppress the generic one to avoid a duplicate title */}
      {/* LEAVE-APPROVALS-UI-CLEANUP-001: Leave Approvals has its own premium header — drop the generic title/subtitle and the
          "N pending" / "x/y online" widgets so the card moves up (effective-scope chip is kept inline below). */}
      {/* TM-NAV-FLICKER-001: the compact header renders for EVERY tab so the sidebar/content never jump */}
      {/* TM-HEADER-REMOVE-001 (Nov 2026): the common "Team Management / Manage leaves…" header and its
          pending/online counters are no longer rendered in embedded mode — every tab has its own title.
          Pending-approval and presence polling (fetchTeamData) is unchanged; only the IT-scope chip remains. */}
      {embedded ? (
        effectiveScope === 'it_manager' ? (
          <div className="px-6 lg:px-8 pt-6">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-400/30 text-[11px] font-semibold text-blue-500"
              data-testid="effective-scope-indicator"
            >
              <Eye className="w-3 h-3" />
              Viewing as IT Manager scope
            </span>
          </div>
        ) : null
      ) : (
      <header className="bg-white/95 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Left: Back Navigation */}
            <button
              onClick={onNavigateBack}
              data-testid="team-management-back"
              className="flex items-center gap-2 px-2 lg:px-4 py-2 lg:-ml-4 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">Dashboard</span>
            </button>
            
            {/* Center: Title */}
            <div className="lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2 text-center min-w-0">
              <h1 className="text-lg lg:text-2xl font-bold text-slate-900 tracking-tight truncate">Team Management</h1>
              <p className="text-sm text-slate-500 font-medium hidden lg:block">Manage leaves and team availability</p>
            </div>

            {/* Right: Quick Stats */}
            <div className="flex items-center gap-2 lg:gap-3">
              {pendingCount > 0 && (
                <div className="hidden sm:flex items-center gap-2 px-3 lg:px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700">{pendingCount} pending</span>
                </div>
              )}
              <div className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                <UserCheck className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">{loggedInCount}/{totalTeam} online</span>
              </div>
            </div>
          </div>
        </div>
      </header>
      )}

      <div className={embedded ? 'px-6 lg:px-8 py-6' : 'max-w-7xl mx-auto px-6 lg:px-8 py-8'}>
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Sidebar Navigation - TitleFlow Card Style */}
          <div className={`w-full lg:w-72 flex-shrink-0 ${inShell ? "hidden" : ""}`} data-testid="internal-nav-panel">
            <nav className={`bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden static lg:sticky ${embedded ? 'lg:top-4' : 'lg:top-28'}`}>
              {/* Nav Header */}
              <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Navigation</p>
              </div>
              
              {/* Nav Items — grid on mobile, vertical list on desktop */}
              <div className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-1 lg:gap-0 lg:space-y-1">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const showBadge = tab.id === 'approvals' && pendingCount > 0;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      data-testid={`team-tab-${tab.id}`}
                      className={`w-full flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3.5 rounded-xl text-left transition-all duration-200 ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg hidden lg:flex items-center justify-center ${
                        isActive ? 'bg-white/20' : 'bg-slate-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${isActive ? 'text-white' : 'text-slate-900'}`}>
                          {tab.label}
                        </p>
                        <p className={`text-xs hidden lg:block ${isActive ? 'text-blue-100' : 'text-slate-400'}`}>
                          {tab.description}
                        </p>
                      </div>
                      {showBadge && (
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                          isActive ? 'bg-white text-blue-600' : 'bg-red-500 text-white'
                        }`}>
                          {pendingCount}
                        </span>
                      )}
                      {isActive && !showBadge && (
                        <ChevronRight className="w-4 h-4 text-white/70 hidden lg:block" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Team Summary Card */}
              <div className="m-3 mt-0 hidden lg:block">
                <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Team Summary
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between px-3 py-2.5 bg-white rounded-lg border border-slate-200">
                      <span className="text-sm text-slate-600">Team Size</span>
                      <span className="text-lg font-bold text-slate-900">{totalTeam}</span>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2.5 bg-emerald-50 rounded-lg border border-emerald-200">
                      <span className="text-sm text-emerald-700">Online Now</span>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                        <span className="text-lg font-bold text-emerald-700">{loggedInCount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Leave Approvals Tab */}
            {mounted('approvals') && (
              <div className="space-y-4" data-testid="leave-approvals-section" {...panel('approvals')}>
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
                  <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-blue-700">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Leave Approvals</h2>
                        <p className="text-blue-100 mt-0.5">Review and approve team leave requests</p>
                      </div>
                      <button
                        onClick={() => setOnBehalfSignal(s => s + 1)}
                        data-testid="apply-on-behalf-btn"
                        className="ml-auto flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-white text-blue-700 rounded-lg hover:bg-blue-50 shadow"
                      >
                        <UserPlus className="w-4 h-4" />
                        Apply Leave for Employee
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    <LeaveApproval currentUser={currentUser} openOnBehalfSignal={onBehalfSignal} onCountsChanged={fetchTeamData} />
                  </div>
                </div>
              </div>
            )}

            {/* Team Calendar Tab */}
            {mounted('calendar') && (
              // TEAM-CAL-UI-001 (Sep 2026): premium Team Leave Calendar (frontend-only; same calendar API)
              <div {...panel('calendar')}><TeamLeaveCalendar /></div>
            )}

            {/* Block Dates Tab */}
            {mounted('blocks') && (
              <div className="space-y-6" {...panel('blocks')}>
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
                  <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-blue-700">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                        <Shield className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Block Leave Dates</h2>
                        <p className="text-blue-100 mt-0.5">Restrict leave applications during critical periods</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <LeaveBlocks currentUser={currentUser} />
                  </div>
                </div>
              </div>
            )}

            {/* IT-BREAK-MGMT-001: Break Management (IT Operations Center) — monitoring only */}
            {mounted('breaks') && (
              <div className="space-y-6" data-testid="break-management-section" {...panel('breaks')}>
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
                  <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-blue-700">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                        <Coffee className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Break Management</h2>
                        <p className="text-blue-100 mt-0.5">Monitor your team's break status and history</p>
                      </div>
                      <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 border border-white/25 text-[11px] font-semibold text-white" data-testid="break-management-readonly">
                        <Eye className="w-3 h-3" />View only
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <ITBreakManagement currentUser={currentUser} />
                  </div>
                </div>
              </div>
            )}

            {/* Session Health Tab - Phase 3 */}
            {mounted('session-status') && (
              <div className="space-y-6" {...panel('session-status')}>
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
                  <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-blue-700">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                        <Activity className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Session Health</h2>
                        <p className="text-blue-100 mt-0.5">Real-time activity monitoring for clocked-in team members</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <TeamSessionStatus currentUser={currentUser} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;
